<template>
    <v-layout
            fill-height
            justify-center
            row
            wrap
    >
        <v-flex xs12 sm10 md10 lg8 xl8
                :class="{'pa-2': !isMobile()}">
            <v-toolbar dense>
                <v-btn flat primary>
                    New
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn color="green darken-1" flat primary @click="updatePlugin()">
                    Save
                </v-btn>
            </v-toolbar>
            <v-card pa-0 class="full-width">
                <!-- PLUGIN COMPILATION RESULT -->
                <v-card v-show="compilation.done" :color="compilation.status" class="white--text">
                    <v-card-text>
                        <v-layout row wrap align-start justify-center>
                            <v-flex shrink mr-2>
                                <v-icon color="white">warning</v-icon>
                            </v-flex>
                            <v-flex xs10>
                                <v-layout column wrap justify-center>
                                    <v-flex xs12>
                                        <div><strong>{{ compilation.title }}</strong></div>
                                        <p style="white-space: normal;"> {{compilation.description}}</p>
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                            <v-flex shrink ml-2>
                                <v-btn small icon @click="compilation.done = false">
                                    <v-icon color="white">
                                        mdi-close
                                    </v-icon>
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
                <!-- PLUGIN HEADER -->
                <v-card-title>
                    <v-layout row wrap>
                        <v-flex xs6 pr-2>
                            <v-text-field
                                    v-model="plugin.pluginName"
                                    label="Name"
                                    required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs6 pl-2>
                            <v-text-field
                                    v-model="plugin.pluginNamespace"
                                    label="Namespace"
                                    required
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12>
                            <v-text-field
                                    v-model="plugin.pluginDescription"
                                    label="Description"
                            ></v-text-field>
                        </v-flex>
                    </v-layout>
                </v-card-title>


                <!-- PLUGIN TEST AND EXECUTION -->
                <v-layout row wrap align-center pa-2>
                    <v-flex grow pl-2>
                        <p class="subheading">Test your code</p>
                    </v-flex>
                    <v-flex shrink>
                        <v-btn flat @click="compilePlugin()">
                            Compile
                            <v-icon dark color="green darken-1">
                                mdi-wrench
                            </v-icon>
                        </v-btn>
                        <v-btn flat @click="executePlugin()">
                            Execute
                            <v-icon dark color="green darken-1">
                                mdi-play-circle
                            </v-icon>
                        </v-btn>
                    </v-flex>
                </v-layout>
                <v-card-text>
                    <v-layout row wrap align-center>
                        <v-flex grow>
                            <p class="subheading">Params</p>
                        </v-flex>
                        <v-flex shrink>
                            <v-btn flat @click="addParam()">
                                Add param
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </v-flex>
                        <v-flex xs12>
                            <v-data-table
                                    v-model="execution.params.selected"
                                    :headers="execution.params.headers"
                                    :items="execution.params.values"
                                    select-all
                                    hide-actions
                                    item-key="name"
                                    class="elevation-0"
                            >
                                <template v-slot:headers="props">
                                    <tr>
                                        <th
                                                v-for="header in props.headers"
                                                :key="header.text"
                                        >
                                            {{ header.text }}
                                        </th>
                                    </tr>
                                </template>
                                <template v-slot:items="props">
                                    <tr>
                                        <td>{{ props.item.name }}</td>
                                        <td class="text-xs-center">
                                            <v-select
                                                    v-model="props.item.type"
                                                    :items="execution.params.types"
                                                    :readonly="props.item.readonly"
                                            ></v-select>
                                        </td>
                                        <td class="text-xs-center">
                                            <v-text-field v-model="props.item.value"
                                                          :readonly="props.item.readonly">
                                            </v-text-field>
                                        </td>
                                        <td class="justify-center layout px-0">
                                            <v-icon v-show="!props.item.readonly" small color="red"
                                                    @click="deleteParam(props.item)">
                                                mdi-delete
                                            </v-icon>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-card-text v-show="execution.executionResult">
                    <codemirror ref="resultViewer"
                                :value="execution.executionResult"
                                :options="execution.resultViewer.options"
                                @ready="onCmReady"
                                @focus="onCmFocus"
                                @input="onCmCodeChange">
                    </codemirror>
                </v-card-text>

                <!-- PLUGIN CODE GENERATOR -->
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12>
                            <v-layout row wrap align-center>
                                <v-flex xs6>
                                    <p class="subheading">Add Code</p>
                                </v-flex>
                                <v-flex xs6>
                                    <SelectImageChooser :options="templates.templateOptions"
                                                        :currentSelection="templates.selectedTemplate"
                                                        :items="templates.availableTemplates">
                                    </SelectImageChooser>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- PLUGIN CODE EDITOR -->
            <v-card class="mt-4">
                <v-card-title>
                    <strong>{{ plugin.pluginName }}</strong>
                    <v-spacer></v-spacer>
                    id: {{ plugin.pluginNamespace }}/{{ plugin.pluginName }}
                </v-card-title>
                <v-card-text class="pa-0">
                    <!-- quill-editor -->
                    <codemirror ref="codeEditor"
                                :value="plugin.pluginCode"
                                :options="codeEditor.options"
                                @ready="onCmReady"
                                @focus="onCmFocus"
                                @input="onCmCodeChange">
                    </codemirror>
                </v-card-text>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>
    import {mapGetters, mapState} from "vuex";

    import APILoaderDecoratorService from "@/helpers/APILoaderDecoratorService";
    import TimeService from "@/helpers/TimeService";
    import UrlsService from "@/helpers/UrlsService";
    import TextService from "@/helpers/TextService";
    import SelectImageChooser from "@/components/Inputs/SelectImageChooser";
    import PluginTemplates from "./pluginTemplate.json";


    // Code Mirror
    // require component
    import {codemirror} from 'vue-codemirror'
    // require styles
    import 'codemirror/lib/codemirror.css'
    // theme css// language
    import 'codemirror/mode/javascript/javascript.js'
    // theme css
    import 'codemirror/theme/monokai.css'
    import 'codemirror/theme/paraiso-light.css'
    // active-line.js
    import 'codemirror/addon/selection/active-line.js'
    // styleSelectedText
    import 'codemirror/addon/selection/mark-selection.js'
    import 'codemirror/addon/search/searchcursor.js'
    // highlightSelectionMatches
    import 'codemirror/addon/scroll/annotatescrollbar.js'
    import 'codemirror/addon/search/matchesonscrollbar.js'
    import 'codemirror/addon/search/searchcursor.js'
    import 'codemirror/addon/search/match-highlighter.js'
    // keyMap
    import 'codemirror/mode/clike/clike.js'
    import 'codemirror/addon/edit/matchbrackets.js'
    import 'codemirror/addon/comment/comment.js'
    import 'codemirror/addon/dialog/dialog.js'
    import 'codemirror/addon/dialog/dialog.css'
    import 'codemirror/addon/search/searchcursor.js'
    import 'codemirror/addon/search/search.js'
    import 'codemirror/keymap/sublime.js'
    // foldGutter
    import 'codemirror/addon/fold/foldgutter.css'
    import 'codemirror/addon/fold/brace-fold.js'
    import 'codemirror/addon/fold/comment-fold.js'
    import 'codemirror/addon/fold/foldcode.js'
    import 'codemirror/addon/fold/foldgutter.js'
    import 'codemirror/addon/fold/indent-fold.js'
    import 'codemirror/addon/fold/markdown-fold.js'
    import 'codemirror/addon/fold/xml-fold.js'
    import ContentToolbar from "../../../Layout/ContentToolbar";


    export default {
        name: "PluginDetailsTraining",
        components: {
            ContentToolbar,
            SelectImageChooser,
            codemirror
        },
        props: {
            pluginId: String, pluginType: String
        },
        data() {
            return {
                plugin: {
                    pluginType: "",
                    pluginId: "",
                    pluginName: "",
                    pluginNamespace: "",
                    pluginDescription: "",
                    pluginCode: "",
                },
                compilation: {
                    done: false,
                    status: "",
                    description: "",
                },
                execution: {
                    paramStartCounter: 2,
                    params: {
                        selected: [],
                        types: ['String', 'Number', 'Boolean', 'Array', 'Object'],
                        headers: [
                            {text: 'Param', value: 'name', sortable: false},
                            {text: 'Type', value: 'type', sortable: false},
                            {text: 'Value', value: 'value', sortable: false},
                            {text: 'Action', value: 'value', sortable: false}
                        ],
                        values: [
                            {
                                name: 'params[0]',
                                type: 'String',
                                value: 'userId',
                                readonly: true
                            },
                            {
                                name: 'params[1]',
                                type: 'String',
                                value: 'sessionId',
                                readonly: true
                            },
                            {
                                name: 'params[2]',
                                type: 'String',
                                value: 'additionalElement',
                                readonly: false
                            }
                        ]
                    },
                    executionResult: undefined,
                    resultViewer: {
                        options: {
                            // codemirror options
                            theme: 'monokai',
                            tabSize: 2,
                            mode: 'text/javascript',
                            styleActiveLine: true,
                            lineNumbers: true,
                            line: true,
                            readOnly: true,
                            keyMap: "sublime",
                            matchBrackets: true,
                            showCursorWhenSelecting: true,
                            foldGutter: true,
                            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
                            //highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
                            styleSelectedText: true,
                            extraKeys: {
                                "Ctrl": "autocomplete",
                                'F11'(cm) {
                                    cm.setOption("fullScreen", !cm.getOption("fullScreen"))
                                },
                                'Esc'(cm) {
                                    if (cm.getOption("fullScreen")) cm.setOption("fullScreen", false)
                                }
                            },
                            hintOptions: {
                                completeSingle: false
                            }
                        }
                    }
                },
                codeEditor: {
                    options: {
                        // codemirror options
                        theme: 'monokai',
                        tabSize: 2,
                        mode: 'text/javascript',
                        styleActiveLine: true,
                        lineNumbers: true,
                        line: true,
                        keyMap: "sublime",
                        matchBrackets: true,
                        showCursorWhenSelecting: true,
                        foldGutter: true,
                        gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
                        //highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
                        styleSelectedText: true,
                        extraKeys: {
                            "Ctrl": "autocomplete",
                            'F11'(cm) {
                                cm.setOption("fullScreen", !cm.getOption("fullScreen"))
                            },
                            'Esc'(cm) {
                                if (cm.getOption("fullScreen")) cm.setOption("fullScreen", false)
                            }
                        },
                        hintOptions: {
                            completeSingle: false
                        }
                    }
                },
                templates: {
                    selectedTemplate: {
                        name: "",
                        template: "",
                        image: "",
                    },
                    templateOptions: {
                        label: this.$t('training.plugins.templates.chooseTemplate'),
                        solo: true,
                        changeCallback: this.putTemplateCode,
                    },
                    availableTemplates: [
                        {
                            name: "REST WebClient",
                            template: "WebClient",
                            //image: require("@/assets/img/plugins/templates/WebClient_logo.png"),
                        },
                        {
                            name: "MongoDB client",
                            template: "MongoDBClient",
                            //image: require("@/assets/img/plugins/templates/MongoDBClient_logo.png"),
                        },
                        {
                            name: "MySQL Client",
                            template: "MySQLClient",
                            //image: require("@/assets/img/plugins/templates/MySQLClient_logo.png"),
                        },
                    ]
                }
            };
        },
        created() {
            this.plugin.pluginId = this.$route.params.pluginId;
            this.plugin.pluginName = this.$route.params.pluginName || "";
            this.plugin.pluginNamespace = this.$route.params.pluginNamespace || "";
            this.plugin.pluginType = this.$route.params.pluginType || "plugins";
            this.getPlugin();
            if (this.plugin.pluginType === "plugins") {
                this.execution.params.values[0].value = this.userInfo.username;
                this.execution.params.values[1].value = this.userOptions.contextId || "Start a conversation";
                this.execution.params.values[2].value = "Put here anything, or remove it";
                this.paramStartCounter = this.execution.params.values.length;
            } else {
                this.execution.params.values = [];
                this.paramStartCounter = 0;
            }

        },
        computed: {
            ...mapGetters("UserState", {
                    userOptions: "getUserOptions",
                    userInfo: "getUserInfo",
                    userSettings: "getSettings",
                }
            ),
            ...mapGetters("AppState", {
                    navigationData: "getNavigationData"
                }
            ),
            codemirror() {
                return this.$refs.codeEditor.codemirror
            },
            resultViewer() {
                return this.$refs.resultViewer.codemirror
            }
        },
        methods: {
            isMobile() {
                return this.$vuetify.breakpoint.name === "xs";
            },
            onCmReady(cm) {
            },
            onCmFocus(cm) {
            },
            onCmCodeChange(newCode) {
            },
            addParam() {
                let newParam = {
                    name: 'params[' + this.execution.params.values.length + ']',
                    type: '',
                    value: '',
                    readonly: false
                };
                this.execution.params.values.push(newParam)
            },
            deleteParam(param) {
                const index = this.execution.params.values.indexOf(param);
                this.execution.params.values.splice(index, 1)
            },
            getPlugin() {
                // Compute plugins
                let requestUrl = {
                    url: UrlsService.plugins,
                    timeout: TimeService.secondsToMillis(0),
                };
                let requestBody = {
                    interactionData: {
                        pluginsInteractionRequest: {
                            pluginOperation: "getPlugin",
                            plugin: {
                                info: {
                                    type: this.plugin.pluginType,
                                    id: this.plugin.pluginId,
                                    name: this.plugin.pluginName,
                                    namespace: this.plugin.pluginNamespace
                                }
                            }
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestBody,
                    },
                };
                let messages = {
                    title: "Retrieving plugin",
                    loading: "Waiting for plugin: " + this.plugin.pluginId,
                    error: "Retrieving failed",
                    success: "Plugin " + this.plugin.pluginId + " got.",
                };
                let options = {
                    loadingType: "none",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                        let response = responseSuccess;
                        let plugin = response.response.pluginObject.plugins[0];
                        this.plugin.pluginName = plugin.info.name;
                        this.plugin.pluginNamespace = plugin.info.namespace;
                        this.plugin.pluginDescription = plugin.info.description;
                        this.plugin.pluginCode = plugin.code;
                    })
                    .catch(responseError => {
                        this.plugin.pluginName = "New plugin";
                        this.plugin.pluginId = TextService.generateRandomString(16);
                        this.plugin.pluginNamespace = "New namespace";
                        this.plugin.pluginDescription = "Insert a short description";
                        this.plugin.pluginCode = PluginTemplates.baseTemplate;
                    });
            },
            updatePlugin() {
                this.plugin.pluginCode = this.codemirror.getValue();
                // Compute plugins
                let requestUrl = {
                    url: UrlsService.plugins,
                    timeout: TimeService.secondsToMillis(0),
                };
                let requestBody = {
                    interactionData: {
                        pluginsInteractionRequest: {
                            pluginOperation: "updatePlugin",
                            plugin: {
                                info: {
                                    type: this.plugin.pluginType,
                                    id: this.plugin.pluginId,
                                    name: this.plugin.pluginName,
                                    namespace: this.plugin.pluginNamespace,
                                    description: this.plugin.pluginDescription
                                },
                                code: this.plugin.pluginCode
                            }
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestBody,
                    },
                };
                let messages = {
                    title: "Updating plugin",
                    loading: "Waiting for plugin upload: " + this.id,
                    error: "Updating failed",
                    success: "Plugin " + this.id + " updated.",
                };
                let options = {
                    loadingType: "none",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                        let response = responseSuccess;
                        let plugin = response.response.pluginObject.plugins[0];
                        this.plugin.pluginDescription = plugin.info.description;
                        this.plugin.pluginCode = plugin.code;
                    })
                    .catch(responseError => {

                    });
            },
            compilePlugin() {
                this.plugin.pluginCode = this.codemirror.getValue();
                // Compute plugins
                let requestUrl = {
                    url: UrlsService.plugins,
                    timeout: TimeService.secondsToMillis(0),
                };
                let requestBody = {
                    interactionData: {
                        pluginsInteractionRequest: {
                            pluginOperation: "compilePlugin",
                            plugin: {
                                info: {
                                    type: this.plugin.pluginType,
                                    name: this.plugin.pluginName,
                                    namespace: this.plugin.pluginNamespace
                                },
                                code: this.plugin.pluginCode
                            }
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestBody,
                    },
                };
                let messages = {
                    title: "Compiling plugin",
                    loading: "Waiting for plugin compilation: " + this.id,
                    error: "Compilation failed",
                    success: "Plugin " + this.id + " has been compiled successfully.",
                };
                let options = {
                    loadingType: "none",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                        let response = responseSuccess;
                        console.log("code", response.response);
                        let code = response.response.pluginObject.status.code;
                        this.compilation.status = "green";
                        this.compilation.done = true;
                        this.compilation.title = response.response.pluginObject.status.description;
                        this.compilation.description = "";
                    })
                    .catch(responseError => {
                        this.compilation.status = "red";
                        this.compilation.done = true;
                        this.compilation.title = responseError.response.pluginObject.status.error.message;
                        this.compilation.description = responseError.response.pluginObject.status.error.stack;
                    });
            },
            executePlugin() {
                let params = [];
                for (let i = this.paramStartCounter; i < this.execution.params.values.length; i++) {
                    let param = this.execution.params.values[i];
                    param.value = param.value.trim();
                    if (param.type === 'Number') {
                        params.push(parseInt(param.value))
                    } else if (param.type === 'String') {
                        params.push(param.value)
                    } else if (param.type === 'Boolean' && param.value === 'true' || param.value === 'false') {
                        params.push(JSON.parse(param.value));
                    } else if (param.type === 'Object') {
                        params.push(JSON.parse(param.value));
                    } else if (param.type === 'Array') {
                        params.push(JSON.parse(param.value));
                    }
                }
                // Compute plugins
                let requestUrl = {
                    url: UrlsService.getIntegrationService,
                    timeout: TimeService.secondsToMillis(0),
                };
                let requestBody = {
                    interactionData: {
                        language: this.userSettings.chatbot.language.current.locale,
                        integrationInteractionRequest: {
                            plugin: {
                                info: {
                                    type: this.plugin.pluginType,
                                    name: this.plugin.pluginName,
                                    namespace: this.plugin.pluginNamespace
                                }
                            },
                            params: params
                        },
                    },
                };
                let requestObject = {
                    type: "normal",
                    urlsInfo: requestUrl,
                    data: {
                        data: requestBody,
                    },
                };
                let messages = {
                    title: "Retrieving plugin",
                    loading: "Waiting for plugin: " + this.id,
                    error: "Retrieving failed",
                    success: "Plugin " + this.id + " got.",
                };
                let options = {
                    loadingType: "none",
                    timeout: TimeService.secondsToMillis(0),
                };
                APILoaderDecoratorService.setLoader(requestObject, messages, options)
                    .then(responseSuccess => {
                        let response = responseSuccess;
                        this.execution.executionResult = JSON.stringify(response.response.integrationObject, null, 4);
                    })
                    .catch(responseError => {

                    });
            },
            putTemplateCode() {

            }
        }
    };
</script>

<style lang="scss">
    .full-width {
        width: 100%;
    }

    .CodeMirror {
        height: auto;
    }

    .CodeMirror-gutters {
        z-index: 1;
    }

    .CodeMirror-gutter-wrapper {
        z-index: 2;
    }

    .CodeMirror-gutter-elt {
        z-index: 2;
    }


    .CodeMirror-focused .cm-matchhighlight {
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAFklEQVQI12NgYGBgkKzc8x9CMDAwAAAmhwSbidEoSQAAAABJRU5ErkJggg==);
        background-position: bottom;
        background-repeat: repeat-x;
    }

    .cm-matchhighlight {
        background-color: lightgreen
    }

    .CodeMirror-selection-highlight-scrollbar {
        background-color: green
    }
</style>
